// utils/bundleUtils.ts
/**
 * Bundle Status Calculation Utilities
 * Core functions for calculating bundle progress, status, and alerts
 * based on protocol configuration and participant test data.
 */

import {
  ProtocolConfig,
  BundleMilestone,
  ExpectedGap,
  getExpectedBundleCountByDay,
  getBundleMilestoneCollectionAnchor,
  getBundleMilestoneExpectedDay,
  getBundleMilestoneMinimumValidTests,
  getBundleMilestoneProtocolDay,
  getBundleMilestoneTargetSessions,
  getBundleMilestoneVisitName,
  getBundleMilestoneWindowEndDay,
  getBundleMilestoneWindowStartDay,
} from '../types/protocolConfig';
import { VvpResult } from '../interfaces/participantTrackerTypes';
import { addDays, fromUnixTime } from 'date-fns';
import { addETCalendarDays, differenceInETCalendarDays, getETCalendarDayKey } from './dateUtils';

// ============================================================
// TYPES
// ============================================================

/**
 * State of a single bundle
 */
export enum BundleState {
  NOT_STARTED = 'Not Started',
  ACTIVE = 'Active',           // Within window, any test count
  EXPIRING = 'Expiring Soon',  // Within window, ≤X days left, <target tests
  COMPLETE = 'Complete',       // Target tests achieved (e.g., 10/10)
  PARTIAL = 'Partial',         // Window closed, 6-9 tests (usable)
  EXPIRED = 'Expired',         // Window closed, <6 tests (data lost)
  FUTURE = 'Future',           // Not yet expected by protocol schedule
}

/**
 * Single bundle data structure
 */
export interface BundleData {
  /** Bundle number (1-indexed, from server) */
  bundleNumber: number;

  /** All test timestamps in this bundle (unix seconds) */
  testTimestamps: number[];

  /** Number of tests in bundle */
  testCount: number;

  /** Date of first test (bundle start) */
  startDate: Date | null;

  /** Window end date (startDate + windowDays) */
  windowEndDate: Date | null;

  /** Study week when bundle was started (null if not started) */
  startWeek: number | null;

  /** Current state of the bundle */
  state: BundleState;

  /** Days remaining in collection window (null if closed/not started) */
  daysRemaining: number | null;

  /** Expected week per protocol */
  expectedWeek: number;

  /** Study year this bundle belongs to */
  studyYear: number;

  /** Is this bundle data usable for analysis? (Complete or Partial) */
  isUsable: boolean;

  /** Is this bundle at protocol target? (10/10) */
  isComplete: boolean;

  /** Associated visit ID for reminders */
  reminderVisitId: string;

  /** Human-readable label */
  label: string;

  /** Session target for THIS bundle (per-milestone override or protocol-wide) */
  targetSessions: number;

  /** Minimum valid tests for THIS bundle (per-milestone override or protocol-wide) */
  minimumValidTests: number;
}

/**
 * Overall bundle status for a participant
 */
export interface BundleStatusInfo {
  /** Current study week (null if pre-baseline) */
  studyWeek: number | null;

  /** Current study day (null if pre-baseline) */
  studyDay: number | null;

  /** All 9 bundles with their current status */
  bundles: BundleData[];

  /** Currently active bundle (if any) */
  currentBundle: BundleData | null;

  /** Count of usable bundles (Complete + Partial) */
  usableCount: number;

  /** Count of complete bundles (10/10 only) */
  completeCount: number;

  /** Count of partial bundles (6-9 tests) */
  partialCount: number;

  /** Count of expired bundles (<6 tests, window closed) */
  expiredCount: number;

  /** Number of bundles expected by current study week */
  expectedByNow: number;

  /** Is participant on track? (usable >= expected) */
  isOnTrack: boolean;

  /** Is participant currently in an expected gap period? */
  isInGap: boolean;

  /** Gap information if currently in gap */
  currentGap: ExpectedGap | null;

  /** Next expected bundle milestone */
  nextMilestone: {
    bundleNumber: number;
    expectedWeek: number;
    visitId: string;
    visitName: string;
    daysUntil: number | null;
  } | null;

  /** Overall status category */
  overallStatus: OverallBundleStatus;

  /** Total tests taken across all home bundles */
  totalTestsTaken: number;

  /** Tests in usable bundles */
  testsInUsableBundles: number;

  /** Tests lost to expired bundles */
  testsLost: number;

  /** Days since last test (null if no tests) */
  daysSinceLastTest: number | null;

  /** Last test date */
  lastTestDate: Date | null;
}

export type BundleComplianceCloseReason =
  | 'tenth_test_collected'
  | 'collection_window_elapsed'
  | 'start_window_closed_without_tests'
  | 'missing_baseline';

export interface BundleComplianceCloseInfo {
  /** Exclusive close boundary — the window counts as closed from this day on.
   * (For 'tenth_test_collected' it is the capping test's instant; its calendar
   * day is both the last open day and the close day.) */
  closeDate: Date | null;
  /** Last calendar day the window was open — what user-facing copy like
   * "open through X" must show. One day before closeDate for day-boundary
   * closes; the capping test's own day for 'tenth_test_collected'. */
  lastOpenDate: Date | null;
  reason: BundleComplianceCloseReason;
}

/**
 * BSR.M01/M02 compliance eligibility uses the Bundle Collection Window close date.
 * Started bundles close when 10 assigned tests are collected or when the actual
 * collection window elapses. Never-started bundles have no collection window, so
 * they become eligible when the Bundle Start Window closes and the cell is Missed.
 */
export const getBundleComplianceCloseInfo = (
  config: ProtocolConfig,
  milestone: BundleMilestone,
  bundle: BundleData | null | undefined,
  baselineDate: Date | null,
): BundleComplianceCloseInfo => {
  if (!baselineDate) {
    return { closeDate: null, lastOpenDate: null, reason: 'missing_baseline' };
  }

  const sortedTimestamps = bundle?.testTimestamps
    ? [...bundle.testTimestamps].sort((a, b) => a - b)
    : [];

  const targetSessions = getBundleMilestoneTargetSessions(config, milestone);
  const isPreAnchor = milestone.windowMode === 'pre_anchor';

  if (sortedTimestamps.length >= targetSessions) {
    const cappingTest = fromUnixTime(sortedTimestamps[targetSessions - 1]);
    return {
      closeDate: cappingTest,
      lastOpenDate: cappingTest,
      reason: 'tenth_test_collected',
    };
  }

  if (sortedTimestamps.length > 0) {
    // Pre-anchor collection closes at the anchor day, not first test + windowDays
    const closeDate = bundle?.windowEndDate
      ?? (isPreAnchor ? baselineDate : addDays(fromUnixTime(sortedTimestamps[0]), config.bundles.windowDays));
    return {
      closeDate,
      // windowEndDate is startDate + windowDays (the first day AFTER the
      // window); the last collectable day is the day before it.
      lastOpenDate: addDays(closeDate, -1),
      reason: 'collection_window_elapsed',
    };
  }

  if (isPreAnchor) {
    // Never-started pre-anchor bundle: its start window closes at the anchor
    // itself; the last collectable day is D-1.
    return {
      closeDate: baselineDate,
      lastOpenDate: addDays(baselineDate, -1),
      reason: 'start_window_closed_without_tests',
    };
  }

  const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
  // windowEndDay is the LAST day of the Bundle Start Window (inclusive offset),
  // so the close boundary is the day after it — matching the exclusive
  // convention of the other branches (and describeBundleForReport's
  // `referenceDay > windowEndDay` Missed check).
  const startWindowLastOpenDay = windowEndDay ?? getBundleMilestoneExpectedDay(milestone);

  return {
    closeDate: addDays(baselineDate, startWindowLastOpenDay + 1),
    lastOpenDate: addDays(baselineDate, startWindowLastOpenDay),
    reason: 'start_window_closed_without_tests',
  };
};

export interface RebucketedHomeTest {
  id: string;
  timestamp: number;
  dayKey: string;
  isEligible: boolean;
}

export interface RebucketedVisitWindowData {
  bundles: { bundleNumber: number; testTimestamps: number[] }[];
  unmatchedTimestamps: number[];
  excludedOutlierTimestamps: number[];
  allActivityTimestamps: number[];
}

/**
 * Overall status categories for quick triage
 */
export enum OverallBundleStatus {
  NOT_STARTED = 'Not Started',      // Pre-baseline or no tests yet
  ON_TRACK = 'On Track',            // Usable >= expected, all complete
  ON_TRACK_PARTIAL = 'On Track (Partial)', // Usable >= expected, some partial
  ACTIVE = 'Active',                // Bundle in progress, on track
  EXPIRING = 'Expiring Soon',       // Current bundle expiring, needs attention
  AT_RISK = 'At Risk',              // Active bundle but behind schedule
  BEHIND = 'Behind',                // Usable < expected
  STALE = 'Stale',                  // No active bundle when one expected
  IN_GAP = 'In Gap',                // In expected gap period, caught up
  COMPLETE = 'Complete',            // All 9 bundles done
}

const getEasternDayKey = getETCalendarDayKey;

// ============================================================
// CORE CALCULATION FUNCTIONS
// ============================================================

/**
 * Calculate study week and day from baseline date
 */
export const calculateStudyPosition = (
  baselineDate: Date | null,
  referenceDate: Date = new Date()
): { studyWeek: number | null; studyDay: number | null } => {
  if (!baselineDate) {
    return { studyWeek: null, studyDay: null };
  }

  const daysSinceBaseline = differenceInETCalendarDays(referenceDate, baselineDate);

  // If before baseline, return negative values
  if (daysSinceBaseline < 0) {
    return { studyWeek: null, studyDay: null };
  }

  const studyWeek = Math.floor(daysSinceBaseline / 7);
  const studyDay = daysSinceBaseline;

  return { studyWeek, studyDay };
};

/**
 * Parse server bundle data into structured bundle objects
 *
 * @param config - Protocol configuration
 * @param serverBundles - Array of { bundleNumber, testTimestamps[] } from server
 * @param baselineDate - Participant's baseline (SV3) date
 * @param referenceDate - Date to calculate status against (default: now)
 */
export const parseBundleData = (
  config: ProtocolConfig,
  serverBundles: { bundleNumber: number; testTimestamps: number[] }[],
  baselineDate: Date | null,
  referenceDate: Date = new Date()
): BundleData[] => {
  const { bundles: bundleConfig } = config;

  // Create a map of server bundles by number for quick lookup
  const serverBundleMap = new Map<number, number[]>();
  serverBundles.forEach(sb => {
    if (sb.bundleNumber > 0) { // Ignore bundle 0 (training/localizer)
      serverBundleMap.set(sb.bundleNumber, sb.testTimestamps);
    }
  });

  // Build bundle data for all expected bundles
  return bundleConfig.milestones.map(milestone => {
    const testTimestamps = serverBundleMap.get(milestone.bundleNumber) || [];
    const testCount = testTimestamps.length;
    const targetSessions = getBundleMilestoneTargetSessions(config, milestone);
    const minimumValidTests = getBundleMilestoneMinimumValidTests(config, milestone);
    const isPreAnchor = milestone.windowMode === 'pre_anchor';

    // Calculate dates
    const sortedTimestamps = [...testTimestamps].sort((a, b) => a - b);
    const startDate = sortedTimestamps.length > 0
      ? fromUnixTime(sortedTimestamps[0])
      : null;
    const collectionStartDate = startDate
      ? getBundleMilestoneCollectionAnchor(milestone) === 'expected_day' && baselineDate
        ? addDays(baselineDate, getBundleMilestoneExpectedDay(milestone))
        : startDate
      : null;
    // Pre-anchor bundles close at the anchor day itself, not first test + windowDays
    const windowEndDate = isPreAnchor
      ? (startDate && baselineDate ? baselineDate : null)
      : collectionStartDate
        ? addETCalendarDays(collectionStartDate, bundleConfig.windowDays)
        : null;

    // Determine state
    const state = determineBundleState(
      config,
      testCount,
      startDate,
      windowEndDate,
      milestone,
      baselineDate,
      referenceDate
    );

    // Calculate days remaining
    let daysRemaining: number | null = null;
    if (windowEndDate && (state === BundleState.ACTIVE || state === BundleState.EXPIRING)) {
      daysRemaining = Math.max(0, differenceInETCalendarDays(windowEndDate, referenceDate));
    }

    // Calculate start week (weeks from baseline when bundle was started)
    let startWeek: number | null = null;
    if (startDate && baselineDate) {
      const daysDiff = differenceInETCalendarDays(startDate, baselineDate);
      startWeek = Math.floor(daysDiff / 7);
    }

    // Determine usability (per-milestone thresholds)
    const isComplete = testCount >= targetSessions;
    const isUsable = testCount >= minimumValidTests;

    return {
      bundleNumber: milestone.bundleNumber,
      testTimestamps: sortedTimestamps,
      testCount,
      startDate,
      windowEndDate,
      startWeek,
      state,
      daysRemaining,
      expectedWeek: milestone.expectedWeek,
      studyYear: milestone.studyYear,
      isUsable: isUsable && (state === BundleState.COMPLETE || state === BundleState.PARTIAL),
      isComplete: isComplete && state === BundleState.COMPLETE,
      reminderVisitId: milestone.reminderVisitId,
      label: milestone.label || `Bundle ${milestone.bundleNumber}`,
      targetSessions,
      minimumValidTests,
    };
  });
};

/**
 * Determine the state of a single bundle
 */
export const determineBundleState = (
  config: ProtocolConfig,
  testCount: number,
  startDate: Date | null,
  windowEndDate: Date | null,
  milestone: BundleMilestone,
  baselineDate: Date | null,
  referenceDate: Date = new Date()
): BundleState => {
  const { bundles: bundleConfig, alerts } = config;
  const { studyWeek, studyDay } = calculateStudyPosition(baselineDate, referenceDate);

  // Pre-baseline: all bundles are future
  if (studyWeek === null) {
    return BundleState.FUTURE;
  }

  // No tests taken yet
  if (!startDate || testCount === 0) {
    const windowStartDay = getBundleMilestoneWindowStartDay(config, milestone);
    const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
    const emptyBundleCloseDay = windowEndDay;

    if (studyDay !== null && emptyBundleCloseDay !== null && studyDay > emptyBundleCloseDay) {
      return BundleState.EXPIRED;
    }

    if (studyDay !== null && studyDay >= windowStartDay) {
      return BundleState.NOT_STARTED; // Expected but not started
    }
    return BundleState.FUTURE; // Not yet expected
  }

  // Per-milestone thresholds (DFI baseline/EFU bundles override the globals)
  const targetSessions = getBundleMilestoneTargetSessions(config, milestone);
  const minimumValidTests = getBundleMilestoneMinimumValidTests(config, milestone);

  // Has tests - check if window is still open
  const windowClosed = windowEndDate
    ? differenceInETCalendarDays(referenceDate, windowEndDate) > 0
    : false;

  if (windowClosed) {
    // Window closed - determine final state based on test count
    if (testCount >= targetSessions) {
      return BundleState.COMPLETE;
    }
    if (testCount >= minimumValidTests) {
      return BundleState.PARTIAL;
    }
    return BundleState.EXPIRED;
  }

  // Window still open
  if (testCount >= targetSessions) {
    return BundleState.COMPLETE; // Achieved target within window
  }

  // Check if expiring soon
  const daysRemaining = windowEndDate ? differenceInETCalendarDays(windowEndDate, referenceDate) : 0;
  if (daysRemaining <= alerts.expiringWarningDays) {
    return BundleState.EXPIRING;
  }

  return BundleState.ACTIVE;
};

/**
 * Check if a given week falls within an expected gap
 */
const isInExpectedGapLocal = (config: ProtocolConfig, studyWeek: number): ExpectedGap | null => {
  return config.bundles.expectedGaps.find(
    gap => studyWeek >= gap.startWeek && studyWeek <= gap.endWeek
  ) ?? null;
};

/**
 * Get next bundle milestone from current week
 */
const getNextMilestoneLocal = (
  config: ProtocolConfig,
  studyDay: number,
  completedBundles: number
): BundleMilestone | null => {
  const nextBySchedule = config.bundles.milestones.find(
    milestone => getBundleMilestoneExpectedDay(milestone) > studyDay
  );
  const nextByCompletion = config.bundles.milestones.find(m => m.bundleNumber > completedBundles);

  if (!nextBySchedule) return nextByCompletion ?? null;
  if (!nextByCompletion) return nextBySchedule;

  return nextBySchedule.bundleNumber <= nextByCompletion.bundleNumber
    ? nextBySchedule
    : nextByCompletion;
};

/**
 * Calculate complete bundle status for a participant
 */
export const calculateBundleStatus = (
  config: ProtocolConfig,
  serverBundles: { bundleNumber: number; testTimestamps: number[] }[],
  baselineDate: Date | null,
  referenceDate: Date = new Date(),
  activityTimestamps: number[] | null = null
): BundleStatusInfo => {
  // Calculate study position
  const { studyWeek, studyDay } = calculateStudyPosition(baselineDate, referenceDate);

  // Parse all bundles
  const bundles = parseBundleData(config, serverBundles, baselineDate, referenceDate);

  // Calculate counts
  const usableCount = bundles.filter(b => b.isUsable).length;
  const completeCount = bundles.filter(b => b.isComplete).length;
  const partialCount = bundles.filter(b => b.state === BundleState.PARTIAL).length;
  const expiredCount = bundles.filter(b => b.state === BundleState.EXPIRED).length;

  // Find current active bundle
  const currentBundle = bundles.find(
    b => b.state === BundleState.ACTIVE || b.state === BundleState.EXPIRING
  ) || null;

  // Calculate expected bundles
  const expectedByNow = studyDay !== null
    ? getExpectedBundleCountByDay(config, studyDay)
    : 0;

  // Check for gap period
  const currentGap = studyWeek !== null
    ? isInExpectedGapLocal(config, studyWeek)
    : null;
  const isInGap = currentGap !== null;

  // Calculate test statistics
  const normalizedActivityTimestamps = activityTimestamps && activityTimestamps.length > 0
    ? [...activityTimestamps].sort((a, b) => a - b)
    : null;

  const totalTestsTaken = normalizedActivityTimestamps
    ? normalizedActivityTimestamps.length
    : bundles.reduce((sum, b) => sum + b.testCount, 0);
  const testsInUsableBundles = bundles
    .filter(b => b.isUsable)
    .reduce((sum, b) => sum + b.testCount, 0);
  const testsLost = bundles
    .filter(b => b.state === BundleState.EXPIRED)
    .reduce((sum, b) => sum + b.testCount, 0);

  // Find last test date
  const allTimestamps = normalizedActivityTimestamps
    ? normalizedActivityTimestamps
    : bundles.reduce<number[]>((acc, b) => acc.concat(b.testTimestamps), []);
  const lastTimestamp = allTimestamps.length > 0
    ? Math.max(...allTimestamps)
    : null;
  const lastTestDate = lastTimestamp ? fromUnixTime(lastTimestamp) : null;
  const daysSinceLastTest = lastTestDate
    ? differenceInETCalendarDays(referenceDate, lastTestDate)
    : null;

  // Calculate next milestone
  let nextMilestone: BundleStatusInfo['nextMilestone'] = null;
  if (studyDay !== null) {
    const nextMilestoneData = getNextMilestoneLocal(config, studyDay, usableCount);
    if (nextMilestoneData) {
      const daysUntilMilestone = baselineDate
        ? differenceInETCalendarDays(
            addDays(baselineDate, getBundleMilestoneExpectedDay(nextMilestoneData)),
            referenceDate
          )
        : null;

      nextMilestone = {
        bundleNumber: nextMilestoneData.bundleNumber,
        expectedWeek: nextMilestoneData.expectedWeek,
        visitId: nextMilestoneData.reminderVisitId,
        visitName: getBundleMilestoneVisitName(nextMilestoneData),
        daysUntil: daysUntilMilestone,
      };
    }
  }

  // Determine overall status
  const isOnTrack = usableCount >= expectedByNow;
  const overallStatus = determineOverallStatus(
    config,
    bundles,
    usableCount,
    completeCount,
    expectedByNow,
    currentBundle,
    isInGap,
    currentGap,
    daysSinceLastTest,
    studyWeek
  );

  return {
    studyWeek,
    studyDay,
    bundles,
    currentBundle,
    usableCount,
    completeCount,
    partialCount,
    expiredCount,
    expectedByNow,
    isOnTrack,
    isInGap,
    currentGap,
    nextMilestone,
    overallStatus,
    totalTestsTaken,
    testsInUsableBundles,
    testsLost,
    daysSinceLastTest,
    lastTestDate,
  };
};

/**
 * Determine overall bundle status for a participant
 */
export const determineOverallStatus = (
  config: ProtocolConfig,
  bundles: BundleData[],
  usableCount: number,
  completeCount: number,
  expectedByNow: number,
  currentBundle: BundleData | null,
  isInGap: boolean,
  currentGap: ExpectedGap | null,
  daysSinceLastTest: number | null,
  studyWeek: number | null
): OverallBundleStatus => {
  const { bundles: bundleConfig, alerts } = config;

  // Pre-baseline
  if (studyWeek === null) {
    return OverallBundleStatus.NOT_STARTED;
  }

  // All bundles complete
  if (usableCount >= bundleConfig.totalExpectedBundles) {
    return OverallBundleStatus.COMPLETE;
  }

  // In expected gap period and caught up
  if (isInGap && currentGap && usableCount >= currentGap.requiredBundlesBeforeGap) {
    return OverallBundleStatus.IN_GAP;
  }

  // Behind schedule (usable < expected)
  if (usableCount < expectedByNow) {
    // Check if there's an active bundle that could catch up
    if (currentBundle) {
      if (currentBundle.state === BundleState.EXPIRING) {
        return OverallBundleStatus.AT_RISK;
      }
      return OverallBundleStatus.BEHIND;
    }
    return OverallBundleStatus.BEHIND;
  }

  // On track - check current bundle status
  if (currentBundle) {
    if (currentBundle.state === BundleState.EXPIRING) {
      return OverallBundleStatus.EXPIRING;
    }
    return OverallBundleStatus.ACTIVE;
  }

  // No active bundle - check if stale
  if (daysSinceLastTest !== null && daysSinceLastTest > alerts.staleDays) {
    // Check if we should have started another bundle
    const notStartedBundles = bundles.filter(
      b => b.state === BundleState.NOT_STARTED && b.expectedWeek <= studyWeek
    );
    if (notStartedBundles.length > 0) {
      return OverallBundleStatus.STALE;
    }
  }

  // On track with some partial bundles
  if (usableCount >= expectedByNow && completeCount < usableCount) {
    return OverallBundleStatus.ON_TRACK_PARTIAL;
  }

  return OverallBundleStatus.ON_TRACK;
};

// ============================================================
// FORMATTING UTILITIES
// ============================================================

/**
 * Format bundle state for display
 */
export const formatBundleState = (state: BundleState): string => {
  return state;
};

/**
 * Get color scheme for bundle state
 */
export const getBundleStateColor = (state: BundleState): {
  bg: string;
  text: string;
  border: string;
  icon: string;
} => {
  switch (state) {
    case BundleState.COMPLETE:
      return { bg: 'green.50', text: 'green.700', border: 'green.500', icon: '✓' };
    case BundleState.PARTIAL:
      return { bg: 'yellow.50', text: 'yellow.700', border: 'yellow.500', icon: '◐' };
    case BundleState.ACTIVE:
      return { bg: 'blue.50', text: 'blue.700', border: 'blue.500', icon: '●' };
    case BundleState.EXPIRING:
      return { bg: 'orange.50', text: 'orange.700', border: 'orange.500', icon: '⏳' };
    case BundleState.EXPIRED:
      return { bg: 'red.50', text: 'red.700', border: 'red.500', icon: '✗' };
    case BundleState.NOT_STARTED:
      return { bg: 'red.50', text: 'red.600', border: 'red.400', icon: '○' };
    case BundleState.FUTURE:
    default:
      return { bg: 'gray.50', text: 'gray.500', border: 'gray.300', icon: '○' };
  }
};

/**
 * Get color scheme for overall status
 */
export const getOverallStatusColor = (status: OverallBundleStatus): {
  colorScheme: string;
  icon: string;
} => {
  switch (status) {
    case OverallBundleStatus.COMPLETE:
      return { colorScheme: 'green', icon: '✓' };
    case OverallBundleStatus.ON_TRACK:
      return { colorScheme: 'green', icon: '✓' };
    case OverallBundleStatus.ON_TRACK_PARTIAL:
      return { colorScheme: 'green', icon: '◐' };
    case OverallBundleStatus.ACTIVE:
      return { colorScheme: 'blue', icon: '●' };
    case OverallBundleStatus.IN_GAP:
      return { colorScheme: 'gray', icon: '—' };
    case OverallBundleStatus.EXPIRING:
      return { colorScheme: 'orange', icon: '⏳' };
    case OverallBundleStatus.AT_RISK:
      return { colorScheme: 'orange', icon: '⚠' };
    case OverallBundleStatus.BEHIND:
      return { colorScheme: 'red', icon: '↓' };
    case OverallBundleStatus.STALE:
      return { colorScheme: 'red', icon: '!' };
    case OverallBundleStatus.NOT_STARTED:
    default:
      return { colorScheme: 'gray', icon: '—' };
  }
};

/**
 * Format study week for display
 */
export const formatStudyWeek = (studyWeek: number | null): string => {
  if (studyWeek === null) return '—';
  return `Week ${studyWeek}`;
};

/**
 * Format current bundle progress for display
 */
export const formatCurrentBundleProgress = (
  bundle: BundleData | null,
  config: ProtocolConfig
): string => {
  if (!bundle) return '—';

  const targetTestsPerBundle = bundle.targetSessions ?? config.bundles.targetTestsPerBundle;

  if (bundle.state === BundleState.COMPLETE) {
    return `${bundle.testCount}/${targetTestsPerBundle} ✓`;
  }

  if (bundle.state === BundleState.ACTIVE || bundle.state === BundleState.EXPIRING) {
    const daysText = bundle.daysRemaining !== null
      ? `(${bundle.daysRemaining}d)`
      : '';
    return `${bundle.testCount}/${targetTestsPerBundle} ${daysText}`;
  }

  return `${bundle.testCount}/${targetTestsPerBundle}`;
};

const parseFlagNumber = (value: string | number | boolean | null | undefined): number => {
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

// ============================================================
// TEST CLASSIFICATION HELPERS
// ============================================================

export const isHomeResult = (result: VvpResult): boolean => {
  return result.bundle_layout?.toLowerCase().includes('home') ?? false;
};

export const isArchivedResult = (result: VvpResult): boolean => {
  return parseFlagNumber(result.is_archived) === 1;
};

/** True when auto-flag and override disagree → test is an outlier */
export const isOutlierResult = (result: VvpResult): boolean => {
  return parseFlagNumber(result.auto_flagged_as_outlier) !== parseFlagNumber(result.override_toggle_outlier);
};

/** Classify a VvpResult by its bundle_layout name */
export const getTestType = (result: VvpResult): 'Training' | 'Localizer' | 'Home' | 'Unknown' => {
  const layout = (result.bundle_layout || '').toLowerCase();
  if (layout.includes('home')) return 'Home';
  if (layout.includes('localizer')) return 'Localizer';
  if (layout.includes('training')) return 'Training';
  return 'Unknown';
};

/**
 * Count assigned/outlier/reliable home tests for a rebucketed bundle.
 * Outliers are included in the assigned total per VVP.O01.
 */
export function countTestsInWindow(
  vvpResults: VvpResult[] | null,
  bundleAssignedTimestamps: number[],
  collectionStartTs: number | null,
  collectionEndTs: number | null,
): { total: number; outliers: number; reliable: number } {
  const total = bundleAssignedTimestamps.length;
  if (!vvpResults || !collectionStartTs || !collectionEndTs || total === 0) {
    return { total, outliers: 0, reliable: total };
  }

  let outliers = 0;
  const assignedTimestamps = new Set(bundleAssignedTimestamps);
  for (const r of vvpResults) {
    if (isArchivedResult(r)) continue;
    if (!isHomeResult(r)) continue;
    const ts = parseInt(r.test_unix_time, 10);
    if (isNaN(ts) || ts < collectionStartTs || ts > collectionEndTs) continue;
    if (assignedTimestamps.has(ts) && isOutlierResult(r)) outliers++;
  }

  return { total, outliers, reliable: total - outliers };
}

// ============================================================
// WINDOW CALCULATIONS (protocol-day space, 1-indexed)
// ============================================================

/**
 * Compute the bundle start window in protocol-day units (1-indexed).
 * Returns the range of days during which a participant may begin a bundle.
 */
export function getBundleStartWindowDays(
  config: ProtocolConfig,
  milestone: BundleMilestone,
): { start: number; target: number; end: number } {
  const target = getBundleMilestoneProtocolDay(milestone);
  const windowStartDay = getBundleMilestoneWindowStartDay(config, milestone);
  const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
  // 0-indexed offset → protocol day. There is no D0: non-negative offsets
  // shift up by one, negative offsets (pre-anchor days) already ARE protocol
  // days (offset -1 = D-1).
  const toProtocolDay = (offset: number): number => (offset >= 0 ? offset + 1 : offset);
  return {
    start: toProtocolDay(windowStartDay),
    target,
    end: windowEndDay !== null ? toProtocolDay(windowEndDay) : target,
  };
}

/**
 * Compute the collection window in protocol-day units.
 * Anchored to the first home test within the start window.
 * Returns null if no test falls within the start window.
 *
 * @param allHomeDays - sorted array of protocol days with home test activity
 * @param assignedHomeDays - optional bundle-assigned protocol days; when provided,
 *   the visual window closes on the 10th assigned test if that happens first
 */
export function getCollectionWindowDays(
  config: ProtocolConfig,
  milestone: BundleMilestone,
  allHomeDays: number[],
  assignedHomeDays?: number[],
): { start: number; end: number } | null {
  const startWindow = getBundleStartWindowDays(config, milestone);
  const isPreAnchor = milestone.windowMode === 'pre_anchor';
  const targetSessions = getBundleMilestoneTargetSessions(config, milestone);
  // Pre-anchor collection never extends past the window end (the day before
  // the anchor); standard bundles run windowDays from the first test.
  const collectionCutoff = isPreAnchor
    ? startWindow.end
    : startWindow.end + config.bundles.windowDays - 1;
  const assignedDays = assignedHomeDays
    ? assignedHomeDays
        .filter(day => day >= startWindow.start && day <= collectionCutoff)
        .sort((a, b) => a - b)
    : null;

  if (assignedDays && assignedDays.length === 0) return null;

  const firstTestDay = assignedDays
    ? assignedDays[0]
    : allHomeDays.find(d => d >= startWindow.start);
  if (firstTestDay === undefined || firstTestDay > startWindow.end) return null;

  const naturalEnd = isPreAnchor
    ? startWindow.end
    : firstTestDay + config.bundles.windowDays - 1;
  const tenthTestDay = assignedDays && assignedDays.length >= targetSessions
    ? assignedDays[targetSessions - 1]
    : null;

  return {
    start: firstTestDay,
    end: tenthTestDay === null ? naturalEnd : Math.min(naturalEnd, tenthTestDay),
  };
}

// ============================================================
// REBUCKETING
// ============================================================

const collectHomeTestsForRebucketing = (
  vvpResults: VvpResult[] | null,
  homeTestUnixTimes: number[] | null = null
): {
  allHomeTests: RebucketedHomeTest[];
  eligibleTests: RebucketedHomeTest[];
  excludedOutlierTimestamps: number[];
  allActivityTimestamps: number[];
} => {
  if (!vvpResults || vvpResults.length === 0) {
    const timestamps = (homeTestUnixTimes || []).slice().sort((a, b) => a - b);
    const allHomeTests = timestamps.map((timestamp, index) => ({
      id: `home-${index}-${timestamp}`,
      timestamp,
      dayKey: getEasternDayKey(fromUnixTime(timestamp)),
      isEligible: true,
    }));

    return {
      allHomeTests,
      eligibleTests: allHomeTests,
      excludedOutlierTimestamps: [],
      allActivityTimestamps: timestamps,
    };
  }

  const homeResults = vvpResults
    .filter(isHomeResult)
    .filter(result => !isArchivedResult(result))
    .map((result, index) => {
      const timestamp = parseInt(result.test_unix_time, 10);

      return {
        result,
        timestamp,
        id: `${result.test_id || index}-${result.test_unix_time}`,
        dayKey: Number.isNaN(timestamp) ? '' : getEasternDayKey(fromUnixTime(timestamp)),
      };
    })
    .filter(entry => !Number.isNaN(entry.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp);

  if (homeResults.length === 0) {
    const timestamps = (homeTestUnixTimes || []).slice().sort((a, b) => a - b);
    const allHomeTests = timestamps.map((timestamp, index) => ({
      id: `home-${index}-${timestamp}`,
      timestamp,
      dayKey: getEasternDayKey(fromUnixTime(timestamp)),
      isEligible: true,
    }));

    return {
      allHomeTests,
      eligibleTests: allHomeTests,
      excludedOutlierTimestamps: [],
      allActivityTimestamps: timestamps,
    };
  }

  const allHomeTests = homeResults.map(entry => ({
    id: entry.id,
    timestamp: entry.timestamp,
    dayKey: entry.dayKey,
    isEligible: true,
  }));

  return {
    allHomeTests,
    eligibleTests: allHomeTests.filter(test => test.isEligible),
    excludedOutlierTimestamps: allHomeTests
      .filter(test => !test.isEligible)
      .map(test => test.timestamp),
    allActivityTimestamps: homeResults.map(entry => entry.timestamp),
  };
};

export const rebucketHomeTestsToVisitWindows = (
  config: ProtocolConfig,
  vvpResults: VvpResult[] | null,
  baselineDate: Date | null,
  homeTestUnixTimes: number[] | null = null
): RebucketedVisitWindowData => {
  const {
    allHomeTests,
    eligibleTests,
    excludedOutlierTimestamps,
    allActivityTimestamps,
  } = collectHomeTestsForRebucketing(vvpResults, homeTestUnixTimes);

  const usedTestIds = new Set<string>();
  const collectionWindowDays = config.bundles.windowDays;

  const takeCollectableTests = (tests: RebucketedHomeTest[], targetTests: number): RebucketedHomeTest[] => {
    const testsByDay = new Map<string, number>();
    const collected: RebucketedHomeTest[] = [];

    for (const test of tests) {
      const countForDay = testsByDay.get(test.dayKey) || 0;
      if (countForDay >= 2) {
        continue;
      }

      collected.push(test);
      testsByDay.set(test.dayKey, countForDay + 1);

      if (collected.length >= targetTests) {
        break;
      }
    }

    return collected;
  };

  const bundles = config.bundles.milestones.map(milestone => {
    if (!baselineDate) {
      return { bundleNumber: milestone.bundleNumber, testTimestamps: [] };
    }

    const windowStartDate = addDays(
      baselineDate,
      getBundleMilestoneWindowStartDay(config, milestone)
    );
    const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
    const windowEndDate = windowEndDay === null
      ? null
      : addDays(baselineDate, windowEndDay);
    const windowStartDayKey = getEasternDayKey(windowStartDate);
    const windowEndDayKey = windowEndDate
      ? getEasternDayKey(windowEndDate)
      : null;

    const collectionAnchor = getBundleMilestoneCollectionAnchor(milestone);

    // Step 1: Find the collection anchor point using the visit window
    const windowTests = collectionAnchor === 'expected_day'
      ? allHomeTests.filter(test => (
          !usedTestIds.has(test.id) &&
          test.dayKey >= windowStartDayKey &&
          (windowEndDayKey === null || test.dayKey <= windowEndDayKey)
        ))
      : allHomeTests.filter(test => (
          !usedTestIds.has(test.id) &&
          test.dayKey >= windowStartDayKey &&
          (windowEndDayKey === null || test.dayKey <= windowEndDayKey)
        ));

    if (windowTests.length === 0) {
      return { bundleNumber: milestone.bundleNumber, testTimestamps: [] };
    }

    const collectionStartDate = collectionAnchor === 'expected_day'
      ? addDays(baselineDate, getBundleMilestoneExpectedDay(milestone))
      : (windowTests.length > 0 ? fromUnixTime(windowTests[0].timestamp) : null);

    if (!collectionStartDate) {
      return { bundleNumber: milestone.bundleNumber, testTimestamps: [] };
    }

    const collectionStartDayKey = getEasternDayKey(collectionStartDate);
    // Pre-anchor collection runs from the first pre-anchor test through the day
    // before the anchor (the window end), NOT first test + windowDays
    const collectionEndDayKey = milestone.windowMode === 'pre_anchor' && windowEndDayKey !== null
      ? windowEndDayKey
      : getEasternDayKey(addDays(collectionStartDate, collectionWindowDays - 1));

    // Step 2: Once collection starts, gather ALL unused tests within the 10-day
    // collection window — the visit window end should not clip the collection period
    const displayTests = takeCollectableTests(
      allHomeTests.filter(test => (
        !usedTestIds.has(test.id) &&
        test.dayKey >= collectionStartDayKey &&
        test.dayKey <= collectionEndDayKey
      )),
      getBundleMilestoneTargetSessions(config, milestone)
    );

    displayTests.forEach(test => usedTestIds.add(test.id));

    return {
      bundleNumber: milestone.bundleNumber,
      testTimestamps: displayTests.map(test => test.timestamp),
    };
  });

  const unmatchedTimestamps = eligibleTests
    .filter(test => !usedTestIds.has(test.id))
    .map(test => test.timestamp)
    .sort((a, b) => a - b);

  return {
    bundles,
    unmatchedTimestamps,
    excludedOutlierTimestamps,
    allActivityTimestamps,
  };
};

// ============================================================
// DATA TRANSFORMATION UTILITIES
// ============================================================

/**
 * Transform raw API data into server bundle format
 * Use this to convert home_test_unix_times with bundle numbers from API
 */
export const transformApiDataToBundles = (
  homeTestUnixTimes: number[] | null,
  bundleAssignments?: { timestamp: number; bundleNumber: number }[]
): { bundleNumber: number; testTimestamps: number[] }[] => {
  if (!homeTestUnixTimes || homeTestUnixTimes.length === 0) {
    return [];
  }

  // If we have explicit bundle assignments from server, use those
  if (bundleAssignments && bundleAssignments.length > 0) {
    const bundleMap = new Map<number, number[]>();

    bundleAssignments.forEach(({ timestamp, bundleNumber }) => {
      const timestamps = bundleMap.get(bundleNumber) || [];
      timestamps.push(timestamp);
      bundleMap.set(bundleNumber, timestamps);
    });

    return Array.from(bundleMap.entries()).map(([bundleNumber, testTimestamps]) => ({
      bundleNumber,
      testTimestamps: testTimestamps.sort((a, b) => a - b),
    }));
  }

  // Fallback: Group by 14-day windows (client-side estimation)
  // Note: This should ideally use server-provided bundle numbers
  const sortedTimestamps = [...homeTestUnixTimes].sort((a, b) => a - b);
  const bundles: { bundleNumber: number; testTimestamps: number[] }[] = [];
  const BUNDLE_WINDOW_SECONDS = 14 * 24 * 60 * 60;

  let currentBundle: number[] = [];
  let bundleStartTime: number | null = null;
  let bundleNumber = 1;

  sortedTimestamps.forEach(timestamp => {
    if (bundleStartTime === null) {
      bundleStartTime = timestamp;
      currentBundle = [timestamp];
    } else if (timestamp <= bundleStartTime + BUNDLE_WINDOW_SECONDS) {
      currentBundle.push(timestamp);
    } else {
      // Save current bundle and start new one
      if (currentBundle.length > 0) {
        bundles.push({ bundleNumber, testTimestamps: currentBundle });
        bundleNumber++;
      }
      bundleStartTime = timestamp;
      currentBundle = [timestamp];
    }
  });

  // Don't forget the last bundle
  if (currentBundle.length > 0) {
    bundles.push({ bundleNumber, testTimestamps: currentBundle });
  }

  return bundles;
};

/**
 * Parse vvp_results array into bundle data structure
 * Uses server-assigned bundle numbers (authoritative source)
 *
 * @param vvpResults - Array of VVP test results from API
 * @returns Array of bundle objects with bundleNumber and test timestamps
 */
export const parseVVPResultsToBundles = (
  vvpResults: VvpResult[] | null
): { bundleNumber: number; testTimestamps: number[] }[] => {
  if (!vvpResults || vvpResults.length === 0) {
    return [];
  }

  const bundleMap = new Map<number, number[]>();

  vvpResults.forEach(result => {
    const bundleNumber = parseInt(result.bundle_number, 10);

    // Skip bundle 0 (training/localizer tests - not home bundles)
    // Also skip if not bundled
    if (bundleNumber === 0 || result.is_bundled !== '1') {
      return;
    }

    const timestamp = parseInt(result.test_unix_time, 10);

    if (isNaN(bundleNumber) || isNaN(timestamp)) {
      return;
    }

    if (!bundleMap.has(bundleNumber)) {
      bundleMap.set(bundleNumber, [timestamp]);
      return;
    }
    const timestamps = bundleMap.get(bundleNumber);
    if (timestamps) timestamps.push(timestamp);
  });

  return Array.from(bundleMap.entries())
    .map(([bundleNumber, testTimestamps]) => ({
      bundleNumber,
      testTimestamps: testTimestamps.sort((a, b) => a - b),
    }))
    .sort((a, b) => a.bundleNumber - b.bundleNumber);
};

export default calculateBundleStatus;
